import { type DraftData, type DraftStorageOptions, DEFAULT_DRAFT_OPTIONS } from './DraftTypes';
import { draftRepository } from './DraftRepository';
import { hydrateToSession } from './sessionHydrator';

/**
 * High-level draft management with auto-save capabilities
 * Provides a clean API with built-in debouncing and error handling
 */
export class DraftManager {
  private autoSaveTimeout: NodeJS.Timeout | null = null;
  private options: DraftStorageOptions;
  private lastSavedHash: string = '';

  constructor(options: Partial<DraftStorageOptions> = {}) {
    this.options = { ...DEFAULT_DRAFT_OPTIONS, debounceMs: 0, ...options };
    console.log('🔧 DRAFT-MANAGER: Initialized with options:', this.options);
  }

  /**
   * Get all drafts, sorted by most recent first
   */
  listDrafts(): DraftData[] {
    return draftRepository.list();
  }

  /**
   * Get a specific draft by ID
   */
  getDraft(id: string): DraftData | null {
    return draftRepository.get(id);
  }

  /**
   * Create a new draft with default values
   */
  createDraft(name: string): DraftData {
    const id = this.generateDraftId();
    const now = new Date().toISOString();

    const draft: DraftData = {
      id,
      name: name.trim() || 'Untitled Campaign',
      currentStep: 0,
      selectedLocations: [],
      formData: {
        companyName: '',
        brandName: '',
        personInCharge: '',
        jobTitle: '',
        email: '',
        mobile: ''
      },
      duration: 14,
      devices: 0,
      creatives: [],
      creativeUrls: [],
      startDate: '',
      endDate: '',
      budgetCalculations: {
        totalCost: 0,
        costPerDevice: 0,
        costPerDay: 0
      },
      locationCreatives: {},
      mediaOwnerCreatives: {},
      useLocationSpecificCreatives: false,
      useMediaOwnerSpecificCreatives: false,
      createdAt: now,
      updatedAt: now
    };

    console.log('🔧 DRAFT-MANAGER: Creating new draft:', draft.id, draft.name);
    this.saveDraft(draft);
    this.setCurrentDraft(draft.id);
    
    return draft;
  }

  /**
   * Save a draft with optional auto-save debouncing
   */
  saveDraft(draft: DraftData, immediate: boolean = false): void {
    if (!draft?.id) {
      console.error('🔧 DRAFT-MANAGER: Cannot save draft without ID');
      return;
    }

    // Calculate hash to detect changes
    const draftHash = this.calculateDraftHash(draft);
    
    if (!immediate && this.options.autoSave) {
      // Use debounced save
      if (draftHash === this.lastSavedHash) {
        console.log('🔧 DRAFT-MANAGER: No changes detected, skipping save');
        return;
      }
      
      this.debouncedSave(draft, draftHash);
    } else {
      // Immediate save
      this.performSave(draft, draftHash);
    }
  }

  /**
   * Delete a draft by ID
   */
  deleteDraft(id: string): void {
    console.log('🔧 DRAFT-MANAGER: Deleting draft:', id);
    
    draftRepository.remove(id);
    
    // Clear current draft if it was the deleted one
    if (this.getCurrentDraftId() === id) {
      this.clearCurrentDraft();
    }
  }

  /**
   * Get the current active draft ID
   */
  getCurrentDraftId(): string | null {
    return draftRepository.getCurrentId();
  }

  /**
   * Set the current active draft
   */
  setCurrentDraft(id: string): void {
    draftRepository.setCurrentId(id);
    
    // Sync to session if enabled
    if (this.options.syncToSession) {
      this.syncDraftToSession(id);
    }
  }

  /**
   * Clear the current draft reference
   */
  clearCurrentDraft(): void {
    draftRepository.clearCurrentId();
    this.clearSession();
  }

  /**
   * Load a draft and sync it to the session/localStorage
   */
  loadDraftToSession(id: string): boolean {
    const draft = this.getDraft(id);
    if (!draft) {
      console.error('🔧 DRAFT-MANAGER: Cannot load draft to session, draft not found:', id);
      return false;
    }

    console.log('🔧 DRAFT-MANAGER: Loading draft to session:', draft.name);
    hydrateToSession(draft);
    this.setCurrentDraft(id);
    
    return true;
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
      this.autoSaveTimeout = null;
    }
    console.log('🔧 DRAFT-MANAGER: Cleaned up resources');
  }

  // Private methods

  private generateDraftId(): string {
    return `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private calculateDraftHash(draft: DraftData): string {
    // Create a simplified hash of the draft for change detection
    const hashData = {
      name: draft.name,
      currentStep: draft.currentStep,
      selectedLocations: draft.selectedLocations,
      formData: draft.formData,
      duration: draft.duration,
      devices: draft.devices,
      useLocationSpecificCreatives: draft.useLocationSpecificCreatives,
      useMediaOwnerSpecificCreatives: draft.useMediaOwnerSpecificCreatives
    };
    
    return JSON.stringify(hashData);
  }

  private debouncedSave(draft: DraftData, hash: string): void {
    // Clear existing timeout
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }

    // Set new timeout
    this.autoSaveTimeout = setTimeout(() => {
      this.performSave(draft, hash);
    }, this.options.debounceMs);
    
    console.log(`🔧 DRAFT-MANAGER: Scheduled auto-save for ${draft.id} in ${this.options.debounceMs}ms`);
  }

  private performSave(draft: DraftData, hash: string): void {
    try {
      console.log('🔧 DRAFT-MANAGER: Saving draft:', draft.id, draft.name);
      
      draftRepository.upsert(draft);
      this.lastSavedHash = hash;
      
      // Sync to session if enabled
      if (this.options.syncToSession) {
        hydrateToSession(draft);
      }
      
      console.log('🔧 DRAFT-MANAGER: Successfully saved draft:', draft.id);
    } catch (error) {
      console.error('🔧 DRAFT-MANAGER: Error saving draft:', error);
    }
  }

  private syncDraftToSession(id: string): void {
    const draft = this.getDraft(id);
    if (draft) {
      hydrateToSession(draft);
    }
  }

  private clearSession(): void {
    // Clear session data when no current draft
    const keysToRemove = [
      'currentStep', 'campaignName', 'selectedLocations', 'formData',
      'duration', 'devices', 'creativeUrls', 'startDate', 'endDate',
      'budgetCalculations', 'locationCreatives', 'mediaOwnerCreatives',
      'useLocationSpecificCreatives', 'useMediaOwnerSpecificCreatives'
    ];

    keysToRemove.forEach(key => {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(key);
      }
    });
    
    console.log('🔧 DRAFT-MANAGER: Cleared session data');
  }
}

// Create default instance
export const draftManager = new DraftManager();